import { Col, Container, Row } from "react-bootstrap";
import React, { ReactNode, useState } from "react";
import CustomButton from "shared/components/button/CustomButton";
import CustomModal from "shared/components/modal/CustomModal";
import { CustomDatePicker } from "shared/components/date/CustomDatePicker";
import { CustomTimePicker } from "shared/components/date/CustomTimePicker";
import CustomSelect from "shared/components/select/CustomSelect";
import { TipoSacado } from "constants/EnumTipoSacado";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { AgendarContatoSchema } from "shared/yup/schemas/AgendarContatoSchema";
import { SelectOptionType } from "types/select/SelectOptionType";
import SelectAsync from "shared/components/select/SelectAsync";
import { api } from "constants/RotasAPI";
import { CheckBox } from "shared/components/checkbox/CheckBox";
import { RegistroCondominioDTO } from "types/dto/RegistroCondominioDTO";
import { RegistroUnidadeDTO } from "types/dto/RegistroUnidadeDTO";
import { RegistroUsuarioDTO } from "types/dto/RegistroUsuarioDTO";
import { AgendamentoContatoDTO } from "types/dto/AgendamentoContatoDTO";
import { formatarDateParaLocalTime } from "shared/util/functions/formatacoesDeValores/FormatarValores";
import { usePostAgendarContato } from "hooks/agendamentoContato/usePostAgendarContatos";
import { toast } from "react-hot-toast";
import showAlert from "shared/util/functions/alert/showAlert";

interface ModalAgendarContatoProps {
  showModal: boolean;
  onCloseModal: () => void;
}

interface AgendamentoContatoForm {
  condominio: SelectOptionType<RegistroCondominioDTO> | null;
  unidade: SelectOptionType<RegistroUnidadeDTO> | null;
  responsavelAgendamento: SelectOptionType<RegistroUsuarioDTO> | null;
  dataAgendamento: Date | null;
  horarioAgendamento: Date;
  sacado: SelectOptionType<TipoSacado> | undefined;
}

const ModalAgendarContato: React.FC<ModalAgendarContatoProps> = (props) => {
  const [cadastrarOutroAgendamento, setCadastrarOutroAgendamento] = useState<boolean>(false);
  const { register, handleSubmit, reset, control, formState, watch, setValue } = useForm<AgendamentoContatoForm>({
    resolver: yupResolver<AgendamentoContatoForm>(AgendarContatoSchema),
  });

  const { mutateAsync: postAgendarContato } = usePostAgendarContato();

  const opcoesTipoSacado: SelectOptionType<TipoSacado>[] = [
    {
      label: "Proprietário",
      value: TipoSacado.PROPRIETARIO,
    },
    {
      label: "Inquilino",
      value: TipoSacado.INQUILINO,
    },
  ];

  const renderFooterModalAdicionarContato = (): ReactNode => {
    return (
      <Row>
        <Col className="d-flex justify-content-end p-0">
          <CustomButton
            id="btn-cancelar-modal-agendar-contato"
            label="Cancelar"
            onClick={() => closeModal()}
            variant="danger-alt"
            className="me-2"
          />
          <CustomButton
            id="btn-cancelar-modal-agendar-contato"
            label="Salvar"
            onClick={handleSubmit(agendarContato)}
            variant="primary"
            className="ps-4 pe-4"
          />
        </Col>
      </Row>
    );
  };

  const agendarContato = async (form: AgendamentoContatoForm) => {
    const agendamentoContatoDTO = {
      dataAgendamento: form.dataAgendamento?.toISOString(),
      horarioAgendamento: formatarDateParaLocalTime(form.horarioAgendamento),
      unidadeId: form.unidade?.value,
      tipoSacado: form.sacado?.value,
      nomeResponsavel: form.responsavelAgendamento?.value?.nome,
      emailResponsavel: form.responsavelAgendamento?.value?.email,
    } as AgendamentoContatoDTO;

    try {
      await postAgendarContato(agendamentoContatoDTO);
      reset();
      if (!cadastrarOutroAgendamento) {
        props.onCloseModal();
      }
    } catch (error: any) {
      if (error?.response?.data?.validation[0]?.message) {
        toast.error(error.response.data.validation[0].message);
      } else {
        toast.error("Erro ao salvar registro.");
      }
    }
  };

  const renderBodyModalAgendarContato = (): ReactNode => {
    return (
      <Container>
        <Row className="mb-3">
          <span>Registre aqui o agendamento de contato com a unidade selecionada</span>
        </Row>
        <Row className="mb-3">
          {renderSelecionarCondominio()}
          {renderSelecionarUnidade()}
        </Row>
        <Row className="mb-3">
          {renderInputSacado()}
          {renderResponsavelAgendamento()}
        </Row>
        <Row className="mb-3">
          {renderDataAgendamento()}
          {renderHorarioAgendamento()}
        </Row>
        <Row className="mb-3">{renderCheckboxCadastrarProximo()}</Row>
      </Container>
    );
  };

  const renderSelecionarCondominio = (): ReactNode => {
    return (
      <Col xl={5} sm={5} xs={5}>
        <SelectAsync
          usarValidacaoParaFormulario
          id="selecionar-condominio"
          name="condominio"
          label="Condomínio"
          endpoint={api.condominio.getRegistrosCondominios}
          onChange={(opcaoSelecionada) => {
            if (watch("unidade")) {
              setValue("unidade", null);
            }
          }}
          control={control}
          register={register}
          setValue={setValue}
          nomeAtributoValue="id"
          nomeAtributoLabel="nomeFantasia"
        />
      </Col>
    );
  };

  const renderSelecionarUnidade = (): ReactNode => {
    return (
      <Col xl={5} sm={5} xs={5}>
        <SelectAsync
          usarValidacaoParaFormulario
          id="unidade"
          name="unidade"
          label="Unidade"
          nomeAtributoValue="id"
          nomeAtributoLabel="blocoNumero"
          endpoint={`${api.unidade.getRegistrosUnidades}/${watch("condominio")?.value}`}
          atributosRefresh={[watch("condominio")?.value]}
          isDisabled={!watch("condominio")?.value}
          control={control}
          register={register}
          setValue={setValue}
        />
      </Col>
    );
  };

  const renderInputSacado = (): ReactNode => {
    return (
      <Col xl={5} sm={5} xs={5}>
        <CustomSelect
          id="tipo-sacado"
          label="Sacado"
          usarValidacaoParaFormulario
          name="sacado"
          register={register}
          control={control}
          options={opcoesTipoSacado}
          setValue={setValue}
        />
      </Col>
    );
  };

  const renderResponsavelAgendamento = (): ReactNode => {
    return (
      <Col xl={5} sm={5} xs={5}>
        <SelectAsync
          usarValidacaoParaFormulario
          id="responsavel-agendamento"
          name="responsavelAgendamento"
          label="Responsável pelo agendamento"
          endpoint={`${api.usuario.getRegistrosUsuarios}`}
          control={control}
          setValue={setValue}
          register={register}
          nomeAtributoLabel="nomeUsuario"
          nomeAtributoValue="nomeEmailUsuario"
        />
      </Col>
    );
  };

  const renderDataAgendamento = (): ReactNode => {
    return (
      <Col xl={5} sm={5} xs={5}>
        <CustomDatePicker
          usarValidacaoParaFormulario
          name="dataAgendamento"
          control={control}
          register={register}
          setValue={setValue}
          label="Data do agendamento"
          popperDirection="right-start"
        />
      </Col>
    );
  };

  const renderHorarioAgendamento = (): ReactNode => {
    return (
      <Col xl={5} sm={5} xs={5}>
        <CustomTimePicker
          usarValidacaoParaFormulario
          label="Horário do agendamento"
          name="horarioAgendamento"
          control={control}
          register={register}
          setValue={setValue}
          popperDirection="right-start"
        />
      </Col>
    );
  };

  const renderCheckboxCadastrarProximo = () => {
    return (
      <Col className="ps-0">
        <CheckBox
          usarValidacaoParaFormulario={false}
          id="cadastrar-proximo"
          value={cadastrarOutroAgendamento}
          onChange={(isChecked) => setCadastrarOutroAgendamento(isChecked)}
        />
        <span>Após salvar cadastrar outro agendamento</span>
      </Col>
    );
  };

  const closeModal = () => {
    const fecharModal = () => {
      reset();
      setCadastrarOutroAgendamento(false);
      props.onCloseModal();
    };
    if (formState.isDirty) {
      showAlert({
        texto: "Existem alterações que não foram salvas na tela. Tem certeza de que deseja sair sem salvar?",
        tipoAlerta: "CONFIRMAR_CANCELAR_ALERT",
        onConfirm: () => fecharModal(),
      });
    } else {
      fecharModal();
    }
  };

  return (
    <CustomModal
      titulo="Agendamentos"
      onCloseModal={() => closeModal()}
      customFooter={renderFooterModalAdicionarContato()}
      showModal={props.showModal}
      size="lg"
      tipoModal="MODAL_LATERAL"
      fecharModalAoClicarFora={!formState.isDirty}
      scrollable
    >
      {renderBodyModalAgendarContato()}
    </CustomModal>
  );
};

export default ModalAgendarContato;
